import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { nsSharedStyles } from '../ns-styles/index.js';

import type { DetailRow } from './ns-detail-list.js';

const CHEVRON = html`
  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" aria-hidden="true">
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="currentColor" />
  </svg>
`;

@customElement('ns-summary-card')
export class NSSummaryCard extends LitElement {
  @property({ type: String })
  heading = '';

  @property({ attribute: false })
  rows: DetailRow[] = [];

  @property({ type: Boolean, reflect: true })
  expandable = false;

  @property({ type: Boolean, reflect: true })
  expanded = false;

  @property({ type: Boolean, reflect: true })
  confirmed = false;

  @property({ type: String, attribute: 'confirm-label' })
  confirmLabel = 'Confirm';

  @query('.details-body')
  private __body?: HTMLElement;

  static styles = [
    nsSharedStyles,
    css`
      :host,
      .wrapper {
        display: flex;
        flex-direction: column;
      }

      .summary {
        z-index: 1;
        display: flex;
        align-items: center;
        padding: var(--wa-space-s);
        border-radius: var(--wa-border-radius-l);
        background-color: var(--wa-color-surface-default);
        color: var(--wa-color-text-normal);
      }

      .heading {
        flex: 1;
      }

      .toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: 0;
        border-radius: var(--wa-border-radius-circle);
        background-color: var(--wa-color-surface-lowered);
        color: var(--wa-color-text-normal);
        transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
      }

      button.toggle {
        cursor: pointer;
      }

      .open .toggle {
        transform: rotate(180deg);
      }

      .details {
        display: grid;
        grid-template-rows: 0fr;
        margin-top: 0;
        border-radius: 0 0 var(--wa-border-radius-l) var(--wa-border-radius-l);
        background-color: var(--wa-color-surface-default);
        transition:
          grid-template-rows 0.4s cubic-bezier(0.23, 1, 0.32, 1),
          margin-top 0.4s cubic-bezier(0.23, 1, 0.32, 1);
      }

      .open .details {
        grid-template-rows: 1fr;
        margin-top: -12px;
      }

      .details-inner {
        min-height: 0;
        overflow: hidden;
      }

      .details-body {
        display: flex;
        flex-direction: column;
        padding: var(--wa-space-l) var(--wa-space-s) var(--wa-space-s);
      }

      .row {
        display: flex;
        align-items: center;
        margin-bottom: var(--wa-space-xs);
      }

      .label {
        margin-right: var(--wa-space-xs);
        font-size: var(--wa-font-size-xs);
        color: var(--wa-color-text-quiet);
      }

      .value {
        padding: var(--wa-space-2xs);
        border-radius: var(--wa-border-radius-pill);
        background: var(--wa-color-surface-lowered);
        color: var(--wa-color-text-normal);
        font-size: var(--wa-font-size-xs);
      }

      .actions {
        display: flex;
      }

      @media (prefers-reduced-motion: reduce) {
        .toggle,
        .details {
          transition: none;
        }
      }
    `,
  ];

  get detailsHeight(): number {
    return this.__body?.scrollHeight ?? 0;
  }

  private __toggle() {
    this.expanded = !this.expanded;
    this.dispatchEvent(new CustomEvent('toggle', { detail: { expanded: this.expanded } }));
  }

  private __confirm() {
    if (this.confirmed) return;
    this.dispatchEvent(new CustomEvent('confirm', { bubbles: true, composed: true }));
  }

  render() {
    const open = !this.expandable || this.expanded;
    const rows = this.rows.filter((row) => row.value !== undefined && row.value !== null && row.value !== '');

    return html`
      <div class=${classMap({ wrapper: true, open })}>
        <div class="summary">
          <div class="heading" id="heading">${this.heading}</div>
          ${this.expandable
            ? html`
                <button
                  type="button"
                  class="toggle"
                  aria-label="Toggle details for ${this.heading}"
                  aria-expanded=${this.expanded ? 'true' : 'false'}
                  aria-controls="details"
                  @click=${this.__toggle}
                >
                  ${CHEVRON}
                </button>
              `
            : html`
                <div class="toggle" aria-hidden="true">${CHEVRON}</div>
              `}
        </div>
        <div class="details" id="details" role="region" aria-labelledby="heading" ?inert=${!open}>
          <div class="details-inner">
            <div class="details-body">
              ${rows.map(
                (row) => html`
                  <div class="row">
                    <div class="label">${row.label}:</div>
                    <div class="value">${row.value}</div>
                  </div>
                `,
              )}
              <div class="actions">
                <button
                  type="button"
                  class="confirm-btn ${this.confirmed ? 'ticked' : ''}"
                  ?disabled=${this.confirmed}
                  aria-label="${this.confirmLabel} ${this.heading}"
                  @click=${this.__confirm}
                >
                  <span class="text">${this.confirmLabel}</span>
                  <img alt="" class="tick-icon" src="/images/icons/checkmark.svg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
